import { msg } from "@lingui/core/macro";
import { createRoute, Outlet, useNavigate, useParams } from "@tanstack/solid-router";
import { appRoute } from "./route";
import { createEffect, createMemo, createSignal, Show, untrack } from "solid-js";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { NotFoundState } from "@/components/ui/not-found-state";
import { useSession } from "@/lib/auth";
import { TodoListView } from "@/features/todos/components/todo-list-view";
import { getTodosCountQuery, getTodosQuery, type ListWithMeta, queryKeys } from "@listto/shared";
import { ListTodo } from "@fleet/icons";
import { useListDetailQuery } from "@listto/shared/solid";
import {
  accountScopedKey,
  createAccountPowerSyncSingle,
} from "@/lib/powersync/create-account-query";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useQueryClient } from "@tanstack/solid-query";

import { useDrizzle } from "@/hooks/use-drizzle";
import type { PowerSyncDrizzleDB } from "@listto/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function ListPage() {
  const { t } = useI18n();
  const params = useParams({ from: "/_app/lists/$listId" });
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const queryClient = useQueryClient();

  const listId = () => params().listId;

  const [lastLoadedListId, setLastLoadedListId] = createSignal<string | null>(null);

  const list = useListDetailQuery({
    db: () => db(),
    drizzle,
    queryFactory: createAccountPowerSyncSingle,
    listId,
    placeholderData: (prev) => {
      if (untrack(listId) === untrack(lastLoadedListId)) {
        return prev;
      }
      return undefined;
    },
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  createEffect(() => {
    const currentListId = listId();
    if (list.data) {
      setLastLoadedListId(currentListId);
    }
  });

  const navigate = useNavigate();

  // Resolves the list: present / loading (sync) / not-found / offline. Avoids
  // leaving "Loading..." forever when the list does not exist or is not accessible.
  const resolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(list.data),
    isPending: () => list.isLoading,
  });

  const currentTitle = createMemo(() => {
    // 1. If active query data is loaded and matches current listId, use it (fully reactive)
    if (list.data && listId() === lastLoadedListId()) {
      return list.data.name;
    }

    // 2. Fallback to list-detail query cache for instant paint during transitions (non-reactive fallback)
    const cachedDetail = queryClient.getQueryData<ListWithMeta>(
      accountScopedKey(userId(), queryKeys.lists.detail(listId())),
    );
    if (cachedDetail?.name) {
      return cachedDetail.name;
    }

    // 3. Fallback to custom-lists query cache for instant paint (non-reactive fallback)
    const uid = userId();
    if (uid) {
      const customLists = queryClient.getQueryData<ListWithMeta[]>(
        queryKeys.lists.customLists(uid),
      );
      const match = customLists?.find((l) => l.id === listId());
      if (match?.name) {
        return match.name;
      }
    }

    return t(msg`Loading...`);
  });

  return (
    <Show
      when={resolution() === "ready" || resolution() === "loading"}
      fallback={
        <NotFoundState
          variant={resolution() === "offline" ? "offline" : "not-found"}
          offerAccountSwitch={resolution() === "not-found"}
          title={resolution() === "offline" ? undefined : t(msg`List not found`)}
          description={
            resolution() === "offline"
              ? undefined
              : t(msg`This list may have been deleted, or you don't have access to it.`)
          }
          primaryAction={{
            label: t(msg`Go to my tasks`),
            onClick: () => {
              void navigate({ to: "/" });
            },
          }}
        />
      }
    >
      <TodoListView
        title={currentTitle() ?? t(msg`Loading...`)}
        icon={ListTodo}
        userId={userId()}
        queryFn={(dbInstance: PowerSyncDrizzleDB, uid: string, options) =>
          getTodosQuery(dbInstance, uid, { ...options, listId: listId() })
        }
        countQueryFn={(dbInstance, uid) =>
          getTodosCountQuery(dbInstance, uid, { listId: listId() })
        }
        defaultAddTodoProps={{ listId: listId() }}
        emptyMessage={t(msg`No tasks in this list.`)}
        listId={listId()}
        adPlacement="list-done"
      />
      <Outlet />
    </Show>
  );
}

export const listRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/lists/$listId",
  component: ListPage,
});
